/** @format */

import Accordion from "../../../../shared/components/accordion";
import QuoteStep from "../../enums/QuoteStep";
import useQuoteStore from "../../store/QuoteStore";
import usePanelStore from "../../store/PanelStore";
import { useCallback } from "react";
import useAppStateStore from "../../../../shared/store/AppStateStore";
import { getInsurerDetailsBySchemeTableId } from "../../../../config/insurers";
import Drawer from "../../../../shared/components/Drawer";

const TqPanelMemberFeatures = () => {
  const STATE = useAppStateStore();
  const quoteStore = useQuoteStore.getState();
  const { get: getPanelData } = usePanelStore();
  const panelQuoteData = getPanelData()?.panelData;
  const closeMoreInfoModal = useCallback(() => {
    STATE.setSelectedCard(null);
    STATE.setFeaturesModal(false);
  }, [STATE.setSelectedCard, STATE.setFeaturesModal]);

  if (!STATE.featuresModal || !STATE.selectedCard) return null;

  const coverTypeMap = {
    "01": "Comprehensive",
    "02": "Third Party and Theft",
    "03": "Third Party Only",
  };
  const quoteRef = panelQuoteData.quoteReference ?? "";
  const coverTypeId = quoteStore.quote.risk.vehicles[0].coverTypeId;
  const coverType = coverTypeMap[coverTypeId] || coverTypeId; // Fallback to ID if mapping not found
  const voluntary = quoteStore.quote.risk.cover.requestedVolExcess;
  const isProtectedNcd =
    quoteStore.quote?.risk?.vehicles?.[0]?.ncdDetails?.protected;
  const insurerDetails = getInsurerDetailsBySchemeTableId(
    STATE.selectedCard?.schemeTableId,
  );
  const compulsoryObj = STATE.selectedCard.panelMemberQuoteExcesses?.find(
    (excess) =>
      excess.excessShortName &&
      excess.excessShortName.toLowerCase() === "compulsory",
  );
  const compulsory = compulsoryObj?.excessAmount || 0;
  const totalExcess = voluntary + compulsory;

  const policyDetailsString = localStorage.getItem("policyDetails");
  const policyDetails = policyDetailsString
    ? JSON.parse(policyDetailsString)
    : null;

  let filteredSchemes;
  if (policyDetails?.uberpolicies?.AllSchemeFeatureDescriptions) {
    filteredSchemes = policyDetails.uberpolicies?.AllSchemeFeatureDescriptions.filter(
      (item) =>
        item.schemeFeatureDescriptionKey.startsWith(
          STATE.selectedCard.schemeTableId,
        ),
    );
  } else {
    console.error(
      "PolicyDetails or AllSchemeFeatureDescriptions is missing or undefined",
    );
  }

  const categorizedFeatures = {
    QuoteFeature: [],
    ImportantTerm: [],
  };

  const filteredFeatures = filteredSchemes?.filter(
    (feature) =>
      ["QuoteFeature", "ImportantTerm"].includes(feature?.category) &&
      !(
        isProtectedNcd === false &&
        feature?.featureName === "Protected No Claims Discount"
      ),
  );

  filteredFeatures?.forEach((feature) => {
    const category = feature?.category;
    if (categorizedFeatures[category]) {
      categorizedFeatures[category]?.push(feature);
    }
  });

  const vehicleUsesCovered = categorizedFeatures.ImportantTerm.find(
    (feature) => feature.featureName === "Vehicle Uses Covered",
  );

  const remainingImportantTerms = categorizedFeatures.ImportantTerm.filter(
    (feature) => feature.featureName !== "Vehicle Uses Covered",
  );

  return (
    <Drawer
      isOpen={STATE.currentStep === QuoteStep.TQ_PANEL}
      onClose={closeMoreInfoModal}
      title="Quote Details"
    >
      <div className="tw-text-dis-text-2">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-dis-border tw-py-2.5 tw-text-base">
          <span className="tw-font-normal">Cover type</span>
          <span className="tw-font-semibold">{coverType}</span>
        </div>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-dis-border tw-py-2.5 tw-text-base">
          <span className="tw-font-normal">Quote reference</span>
          <span className="tw-font-semibold">{quoteRef}</span>
        </div>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-dis-border tw-py-2.5 tw-text-base">
          <span className="tw-font-normal">Insurer name</span>
          <span className="tw-font-semibold">
            {insurerDetails?.displayName ?? ""}
          </span>
        </div>
        <div className="tw-mb-6 tw-flex tw-flex-col tw-items-start tw-gap-3 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-dis-border tw-py-2.5 tw-text-dis-min tw-text-dis-text-muted">
          <span className="tw-text-base tw-font-normal">Insurer information</span>
          <span className="tw-w-full tw-break-words tw-font-normal">
            {insurerDetails?.information ?? ""}
          </span>
        </div>

        <h5 className="tw-mb-[5px] tw-mt-[15px] tw-text-base tw-font-bold tw-text-dis-navy">
          Excess
        </h5>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-dis-border tw-py-2.5 tw-text-base">
          <span className="tw-font-normal">Voluntary</span>
          <span className="tw-font-semibold">£{voluntary}</span>
        </div>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-dis-border tw-py-2.5 tw-text-base">
          <span className="tw-font-normal">Compulsory</span>
          <span className="tw-font-semibold">£{compulsory}</span>
        </div>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-dis-border tw-py-2.5 tw-text-base">
          <span className="tw-font-normal">Total Excess</span>
          <span className="tw-font-semibold">£{totalExcess}</span>
        </div>

        <div>
          {vehicleUsesCovered && (
            <Accordion
              title="Quote Features"
              items={[vehicleUsesCovered]}
              headerVariant="simple"
            />
          )}
          {categorizedFeatures.QuoteFeature.length > 0 && (
            <Accordion
              title=""
              items={categorizedFeatures.QuoteFeature}
              headerVariant="simple"
            />
          )}
          {remainingImportantTerms.length > 0 && (
            <Accordion
              title=""
              items={remainingImportantTerms}
              headerVariant="simple"
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default TqPanelMemberFeatures;
